import { MinusCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Flex,
  InputNumber,
  Popover,
  Radio,
  Select,
  Space,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { debounce } from 'lodash';
import { Repeat } from 'lucide-react';
import {
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { finalize, from, tap } from 'rxjs';
import { ScheduledMealsDataContext } from './scheduled-meals.page';
import { type ServeType } from '../../../../types';
import { type LocalDate } from '../../../../utils/local-date';
import Utils from '../../../../utils/utils';

export interface ScheduledMealRow {
  uid: string;
  id?: number;
  day: string; // 'YYYY-MM-DD'
  mealCategory?: number;
  meal?: number;
  extraMealItemId1?: number;
  extraMealItemId2?: number;
  extraMealItemServeType1?: ServeType | null;
  extraMealItemServeType2?: ServeType | null;
  price?: number;
  order: number;
  isException: boolean;
}

interface RowProps {
  row: ScheduledMealRow;
  index: number;
  dayDate: LocalDate;
  indexInIteration: number;
  selectedCategoryIds: Set<number>;
  onUpdateRow: (rowIndex: number, updated: Partial<ScheduledMealRow>) => void;
  onDeleteRow: (rowIndex: number) => void;
  applyPriceToCategory: (categoryId: number, price: number) => void;
  applyMealToDays: (meal: ScheduledMealRow, startDayIndex: number) => Promise<void>;
  isLoading?: boolean;
}

const SERVE_TYPE_OPTIONS = Object.entries(Utils.serveTypesLabels);
const SERVE_TYPE_FIELD_NAMES = { value: '0', label: '1' } as const;

const renderLabelWithTooltip = (renderProps: { label?: ReactNode }): ReactNode => (
  <Tooltip title={renderProps.label} mouseEnterDelay={0.3}>
    <Typography.Text style={{ lineHeight: '30px', width: '100%', display: 'block' }} ellipsis>
      {renderProps.label}
    </Typography.Text>
  </Tooltip>
);

const renderOptionWithTooltip = (option: any): ReactNode => (
  <Tooltip title={option.label} mouseEnterDelay={0.3}>
    <div
      style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {option.label}
    </div>
  </Tooltip>
);

const ScheduledMealRowComponent = (props: RowProps): ReactNode => {
  const { token } = theme.useToken();
  const {
    row,
    index,
    dayDate,
    indexInIteration,
    selectedCategoryIds,
    onUpdateRow,
    onDeleteRow,
    applyPriceToCategory,
    applyMealToDays,
    isLoading = false,
  } = props;

  const onUpdate = useCallback(
    (partial: Partial<ScheduledMealRow>) => {
      onUpdateRow(index, partial);
    },
    [onUpdateRow, index]
  );

  const onDelete = useCallback(() => {
    onDeleteRow(index);
  }, [onDeleteRow, index]);

  const [popOverOpen, setPopOverOpen] = useState(false);
  const [repeatPopOverOpen, setRepeatPopOverOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('all');

  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const [extra1Open, setExtra1Open] = useState(false);
  const [extra2Open, setExtra2Open] = useState(false);

  const { sortedMealCategories, mealsByCategoryMap, mealMap, mealItemOptions, mealItemMap } =
    useContext(ScheduledMealsDataContext);

  const selectedCategoryId = row.mealCategory ? Number(row.mealCategory) : undefined;
  const currentCategory = sortedMealCategories.find((mc) => mc.id === selectedCategoryId);
  const isGeneric = currentCategory?.isGeneric ?? false;

  const debouncePriceRef = useRef<any>(null);
  useEffect(() => {
    debouncePriceRef.current = debounce((newPrice: number) => {
      if (!row.isException && row.mealCategory && newPrice > 0) {
        setPopOverOpen(true);
      }
    }, 1000);
    return () => {
      debouncePriceRef.current?.cancel();
    };
  }, [row.isException, row.mealCategory]);

  const mealCategoryOptions = useMemo(() => {
    if (categoryOpen) {
      return sortedMealCategories
        .filter(
          (mc) =>
            mc.availableWeekDays[dayDate.day()] === '1' &&
            (mc.id === selectedCategoryId || !selectedCategoryIds.has(mc.id))
        )
        .map((mc) => ({
          value: mc.id,
          label: mc.name,
        }));
    }
    if (currentCategory) {
      return [{ value: currentCategory.id, label: currentCategory.name }];
    }
    return [];
  }, [
    categoryOpen,
    sortedMealCategories,
    dayDate,
    selectedCategoryId,
    selectedCategoryIds,
    currentCategory,
  ]);

  // Lazy option arrays: only populate full lists when dropdown is open
  const mealOptions = useMemo(() => {
    if (mealOpen) {
      return selectedCategoryId ? mealsByCategoryMap.get(selectedCategoryId) ?? [] : [];
    }
    if (row.meal) {
      return [{ value: row.meal, label: mealMap.get(row.meal) ?? '' }];
    }
    return [];
  }, [mealOpen, selectedCategoryId, mealsByCategoryMap, row.meal, mealMap]);

  const extraItem1Options = useMemo(() => {
    if (extra1Open) {
      return mealItemOptions;
    }
    if (row.extraMealItemId1) {
      return [{ value: row.extraMealItemId1, label: mealItemMap.get(row.extraMealItemId1) ?? '' }];
    }
    return [];
  }, [extra1Open, mealItemOptions, row.extraMealItemId1, mealItemMap]);

  const extraItem2Options = useMemo(() => {
    if (extra2Open) {
      return mealItemOptions;
    }
    if (row.extraMealItemId2) {
      return [{ value: row.extraMealItemId2, label: mealItemMap.get(row.extraMealItemId2) ?? '' }];
    }
    return [];
  }, [extra2Open, mealItemOptions, row.extraMealItemId2, mealItemMap]);

  return (
    <Flex align="center" gap="small" style={{ width: '100%' }}>
      {/* Delete button (fixed width 24px) */}
      <Button
        shape="circle"
        size="small"
        type="text"
        icon={<MinusCircleOutlined />}
        onClick={onDelete}
        danger
        disabled={isLoading}
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />

      <Space.Compact style={{ flex: 1, width: '100%' }}>
        {/* Meal Category */}
        <Select
          style={{ flex: 2, minWidth: 120 }}
          showSearch
          placeholder="Select Category"
          optionFilterProp="label"
          options={mealCategoryOptions}
          onDropdownVisibleChange={setCategoryOpen}
          value={row.mealCategory}
          disabled={isLoading}
          status={!row.mealCategory ? 'error' : undefined}
          onChange={(val) => {
            onUpdate({ mealCategory: val, meal: undefined });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Meal */}
        <Select
          style={{ flex: 2.5, minWidth: 140 }}
          showSearch
          allowClear
          placeholder={isGeneric ? 'Not Applicable' : 'Select Meal'}
          optionFilterProp="label"
          options={mealOptions}
          onDropdownVisibleChange={setMealOpen}
          value={row.meal}
          status={!isGeneric && !row.meal ? 'error' : undefined}
          disabled={isLoading || isGeneric || !selectedCategoryId}
          onChange={(val) => {
            onUpdate({ meal: val });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Extra Meal Item 1 */}
        <Select
          style={{ flex: 2, minWidth: 120 }}
          showSearch
          allowClear
          placeholder="None"
          optionFilterProp="label"
          options={extraItem1Options}
          onDropdownVisibleChange={setExtra1Open}
          value={row.extraMealItemId1}
          status={row.extraMealItemServeType1 && !row.extraMealItemId1 ? 'error' : undefined}
          disabled={isLoading}
          onChange={(val) => {
            onUpdate({ extraMealItemId1: val });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Extra Meal Item 1 Serve Type */}
        <Select
          style={{ flex: 1.5, minWidth: 100 }}
          showSearch
          allowClear
          placeholder="None"
          optionFilterProp="0"
          defaultActiveFirstOption
          options={SERVE_TYPE_OPTIONS}
          fieldNames={SERVE_TYPE_FIELD_NAMES}
          value={row.extraMealItemServeType1}
          status={row.extraMealItemId1 && !row.extraMealItemServeType1 ? 'error' : undefined}
          disabled={isLoading}
          onChange={(val) => {
            onUpdate({ extraMealItemServeType1: val });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Extra Meal Item 2 */}
        <Select
          style={{ flex: 2, minWidth: 120 }}
          showSearch
          allowClear
          placeholder="None"
          optionFilterProp="label"
          options={extraItem2Options}
          onDropdownVisibleChange={setExtra2Open}
          value={row.extraMealItemId2}
          status={row.extraMealItemServeType2 && !row.extraMealItemId2 ? 'error' : undefined}
          disabled={isLoading}
          onChange={(val) => {
            onUpdate({ extraMealItemId2: val });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Extra Meal Item 2 Serve Type */}
        <Select
          style={{ flex: 1.5, minWidth: 100 }}
          showSearch
          allowClear
          placeholder="None"
          optionFilterProp="0"
          defaultActiveFirstOption
          options={SERVE_TYPE_OPTIONS}
          fieldNames={SERVE_TYPE_FIELD_NAMES}
          value={row.extraMealItemServeType2}
          status={row.extraMealItemId2 && !row.extraMealItemServeType2 ? 'error' : undefined}
          disabled={isLoading}
          onChange={(val) => {
            onUpdate({ extraMealItemServeType2: val });
          }}
          loading={isLoading}
          labelRender={renderLabelWithTooltip}
          optionRender={renderOptionWithTooltip}
        />

        {/* Price */}
        <Popover
          open={popOverOpen}
          title={null}
          overlayStyle={{ maxWidth: 300 }}
          content={
            <Flex vertical gap="small">
              <Typography.Paragraph style={{ margin: 0 }}>
                Do you want to apply <b>{Utils.currencyFormat(row.price ?? 0)}</b> to all meals of
                category <b>{currentCategory?.name ?? ''}</b>?
              </Typography.Paragraph>
              <Flex style={{ flexDirection: 'row-reverse' }} gap="small">
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    if (row.mealCategory) {
                      applyPriceToCategory(row.mealCategory, row.price ?? 0);
                    }
                    setPopOverOpen(false);
                  }}
                >
                  Apply
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setPopOverOpen(false);
                  }}
                >
                  Dismiss
                </Button>
              </Flex>
            </Flex>
          }
        >
          <InputNumber<number>
            precision={2}
            style={{ width: 90, flexShrink: 0 }}
            min={0}
            value={row.price}
            status={row.price === undefined || row.price === null ? 'error' : undefined}
            formatter={(v, info) =>
              !info.userTyping ? Utils.currencyFormat(Number(v) ?? 0) : info.input
            }
            parser={(str) => Number(str?.replace(/[^0-9.-]+/g, '') ?? 0)}
            onChange={(val) => {
              const numVal = val ?? 0;
              onUpdate({ price: numVal });
              debouncePriceRef.current?.(numVal);
            }}
          />
        </Popover>

        {/* Repeat Meal Popover */}
        <Popover
          open={repeatPopOverOpen}
          placement="top"
          content={
            <Flex vertical gap="small">
              <Typography.Text strong>Repeat this meal</Typography.Text>
              <Radio.Group
                value={selectedOption}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                }}
              >
                <Space direction="vertical">
                  <Radio value="all">Remaining days in current iteration?</Radio>
                </Space>
              </Radio.Group>
              <Flex style={{ flexDirection: 'row-reverse' }} gap="small">
                <Button
                  size="small"
                  type="primary"
                  loading={isLoading}
                  onClick={() => {
                    from(applyMealToDays(row, indexInIteration))
                      .pipe(
                        tap(() => {}),
                        finalize(() => {
                          setRepeatPopOverOpen(false);
                        })
                      )
                      .subscribe();
                  }}
                >
                  Apply
                </Button>
              </Flex>
            </Flex>
          }
          onOpenChange={(open) => {
            setRepeatPopOverOpen(open);
          }}
          title={null}
          trigger="click"
        >
          <Button
            style={{ width: 32, flexShrink: 0, padding: 0 }}
            disabled={row.isException || !row.mealCategory}
            onClick={() => {
              setRepeatPopOverOpen(!repeatPopOverOpen);
            }}
            icon={<Repeat size={16} />}
            title="Repeat meal to following days"
          />
        </Popover>

        {/* Exception Button */}
        <Tooltip
          placement="left"
          title={row.isException ? 'Unmark this meal as Exception' : 'Mark this meal as Exception'}
        >
          <Button
            style={{
              width: 32,
              flexShrink: 0,
              padding: 0,
              ...(row.isException ? { backgroundColor: token.colorPrimary, color: 'white' } : {}),
            }}
            onClick={() => {
              onUpdate({ isException: !row.isException });
            }}
          >
            <b>E</b>
          </Button>
        </Tooltip>
      </Space.Compact>
    </Flex>
  );
};

export default memo(ScheduledMealRowComponent);
